/**
 * Capture Meta click ids on the wallet site so signup can send them to the
 * Conversions API. fbclid from the landing URL becomes fbc. _fbc and _fbp are
 * read from cookies when a pixel on this host has set them.
 */

const FBC_KEY = 'mm_fbc';
const FBP_KEY = 'mm_fbp';
const FBC_COOKIE_MAX_AGE = 90 * 24 * 60 * 60;

const FBC_RE = /^fb\.1\.\d{10,16}\.[A-Za-z0-9_-]{1,200}$/;
const FBP_RE = /^fb\.\d+\.\d{10,16}\.\d{1,20}$/;
const FBCLID_RE = /^[A-Za-z0-9_-]{1,200}$/;

export interface ClickStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

export function isValidFbc(value: string | null | undefined): value is string {
  return typeof value === 'string' && FBC_RE.test(value);
}

export function isValidFbp(value: string | null | undefined): value is string {
  return typeof value === 'string' && FBP_RE.test(value);
}

export function clickIdFromFbc(value: string | null | undefined): string | null {
  if (!isValidFbc(value)) return null;
  const parts = value.split('.');
  return parts.slice(3).join('.') || null;
}

export function buildFbc(fbclid: string, nowMs: number): string | null {
  const id = String(fbclid || '').trim();
  if (!FBCLID_RE.test(id)) return null;
  if (!Number.isFinite(nowMs) || nowMs <= 0) return null;
  return `fb.1.${Math.floor(nowMs)}.${id}`;
}

export function fbcCookieAssignment(fbc: string, secure: boolean): string {
  const secureAttr = secure ? '; Secure' : '';
  return `_fbc=${encodeURIComponent(fbc)}; Path=/; Max-Age=${FBC_COOKIE_MAX_AGE}; SameSite=Lax${secureAttr}`;
}

export function readCookieValue(cookieHeader: string, name: string): string | null {
  if (!cookieHeader) return null;
  const parts = cookieHeader.split(';');
  for (const part of parts) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf('=');
    if (eq <= 0) continue;
    const key = trimmed.slice(0, eq);
    if (key !== name) continue;
    const raw = trimmed.slice(eq + 1);
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  return null;
}

function defaultStorage(): ClickStorage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

function defaultCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  return readCookieValue(document.cookie || '', name);
}

function defaultSetCookie(assignment: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = assignment;
}

function rememberFbc(
  fbc: string,
  storage: ClickStorage | null,
  getCookie: (name: string) => string | null,
  setCookie: (assignment: string) => void,
  secure: boolean,
): void {
  if (storage) {
    try {
      storage.setItem(FBC_KEY, fbc);
    } catch {
      // Private mode can reject localStorage. The cookie is the other copy.
    }
  }
  if (getCookie('_fbc') === fbc) return;
  setCookie(fbcCookieAssignment(fbc, secure));
}

/**
 * Read fbclid from the current URL and keep fbc across in-app route changes.
 * The wallet root redirects to /dashboard and drops the query string, so this
 * must run before that redirect, and again on any later route that still has fbclid.
 */
export function captureMetaClickIds(
  search: string,
  deps?: {
    nowMs?: number;
    secure?: boolean;
    getCookie?: (name: string) => string | null;
    setCookie?: (assignment: string) => void;
    storage?: ClickStorage | null;
  },
): void {
  const storage = deps && 'storage' in deps ? (deps.storage ?? null) : defaultStorage();
  const getCookie = deps?.getCookie || defaultCookie;
  const setCookie = deps?.setCookie || defaultSetCookie;
  const secure = deps?.secure ?? (typeof window !== 'undefined' && window.location.protocol === 'https:');
  const nowMs = deps?.nowMs ?? Date.now();
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const fbclid = params.get('fbclid');

  if (fbclid && FBCLID_RE.test(fbclid)) {
    const cookieFbc = getCookie('_fbc');
    const storedFbc = storage ? storage.getItem(FBC_KEY) : null;
    if (isValidFbc(cookieFbc) && clickIdFromFbc(cookieFbc) === fbclid) {
      rememberFbc(cookieFbc, storage, getCookie, setCookie, secure);
    } else if (isValidFbc(storedFbc) && clickIdFromFbc(storedFbc) === fbclid) {
      rememberFbc(storedFbc, storage, getCookie, setCookie, secure);
    } else {
      const built = buildFbc(fbclid, nowMs);
      if (built) rememberFbc(built, storage, getCookie, setCookie, secure);
    }
  } else {
    const cookieFbc = getCookie('_fbc');
    const storedFbc = storage ? storage.getItem(FBC_KEY) : null;
    if (isValidFbc(cookieFbc)) rememberFbc(cookieFbc, storage, getCookie, setCookie, secure);
    else if (isValidFbc(storedFbc)) rememberFbc(storedFbc, storage, getCookie, setCookie, secure);
  }

  const cookieFbp = getCookie('_fbp');
  if (storage && isValidFbp(cookieFbp)) {
    try {
      storage.setItem(FBP_KEY, cookieFbp);
    } catch {
      // fbp is optional. Signup still sends fbc.
    }
  }
}

export function currentEventSourceUrl(loc?: { origin: string; pathname: string } | null): string | undefined {
  const source = loc !== undefined
    ? loc
    : (typeof window !== 'undefined' ? window.location : null);
  if (!source || !source.origin || !source.pathname) return undefined;
  return `${source.origin}${source.pathname}`;
}

export function readStoredMetaAttribution(deps?: {
  storage?: ClickStorage | null;
  getCookie?: (name: string) => string | null;
  location?: { origin: string; pathname: string } | null;
}): { fbc?: string; fbp?: string; eventSourceUrl?: string } {
  const storage = deps && 'storage' in deps ? deps.storage : defaultStorage();
  const getCookie = deps?.getCookie || defaultCookie;
  const out: { fbc?: string; fbp?: string; eventSourceUrl?: string } = {};
  if (storage) {
    const fbc = storage.getItem(FBC_KEY);
    const fbp = storage.getItem(FBP_KEY);
    if (isValidFbc(fbc)) out.fbc = fbc;
    if (isValidFbp(fbp)) out.fbp = fbp;
  }
  if (!out.fbc) {
    const cookieFbc = getCookie('_fbc');
    if (isValidFbc(cookieFbc)) out.fbc = cookieFbc;
  }
  if (!out.fbp) {
    const cookieFbp = getCookie('_fbp');
    if (isValidFbp(cookieFbp)) out.fbp = cookieFbp;
  }
  const url = currentEventSourceUrl(deps && 'location' in deps ? deps.location : undefined);
  if (url) out.eventSourceUrl = url;
  return out;
}
